// ─── StripeSettingsPage — Admin → Stripe ──────────────────────────────────────
// Super-admin form for managing the Stripe configuration that the
// runtime code reads (`stripe_service.py` prefers DB row over env
// vars). All secrets are stored server-side; the read endpoint only
// returns "•••• last4" so the plaintext never travels back to the
// browser.

import { useEffect, useState } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { Loader, AlertCircle, Check, RefreshCcw, Eye, EyeOff } from 'lucide-react'
import { Navigation } from '../components/Navigation'
import { AppHeader } from '../components/AppHeader'
import useAuthStore from '../store/auth-store'

const API_URL         = import.meta.env.VITE_API_URL         || 'https://wqlive.ezxdemo.com'
const API_BASE_PATH   = import.meta.env.VITE_API_BASE_PATH   || '/api'
const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'

const authHeaders = () => {
  const t = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }
}

// ─── Small form-input widget ──────────────────────────────────────────────────
// Secret fields render as a masked pill until the user chooses to
// change them — clicking "Change" reveals an input that starts empty.
function SecretField({ label, meta, value, onChange, placeholder, helpText }) {
  const [editing, setEditing] = useState(false)
  const [show,    setShow]    = useState(false)
  const has = meta?.set

  if (!editing) {
    return (
      <div>
        <div className="text-xs font-semibold text-gray-700 mb-1">{label}</div>
        <div className="flex items-center gap-2">
          <div className="flex-1 px-3 py-1.5 border border-gray-200 rounded bg-gray-50 text-sm text-gray-700 font-mono">
            {has ? `•••• ${meta.last4}` : <span className="italic text-gray-400">Not set</span>}
          </div>
          <button type="button" onClick={() => setEditing(true)}
                  className="px-3 py-1.5 text-xs font-semibold text-[#358ECD] border border-[#358ECD] rounded hover:bg-[#EBF5FB]">
            {has ? 'Change' : 'Set'}
          </button>
        </div>
        {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
      </div>
    )
  }

  return (
    <div>
      <div className="text-xs font-semibold text-gray-700 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <input
          type={show ? 'text' : 'password'}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm font-mono focus:outline-none focus:border-[#358ECD]"
        />
        <button type="button" onClick={() => setShow((s) => !s)}
                title={show ? 'Hide' : 'Show'}
                className="p-1.5 text-gray-500 hover:text-gray-700">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
        <button type="button" onClick={() => { setEditing(false); onChange(undefined) }}
                className="px-3 py-1.5 text-xs text-gray-600 hover:underline">
          Cancel
        </button>
      </div>
      {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
    </div>
  )
}

function TextField({ label, value, onChange, placeholder, helpText }) {
  return (
    <div>
      <div className="text-xs font-semibold text-gray-700 mb-1">{label}</div>
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm font-mono focus:outline-none focus:border-[#358ECD]"
      />
      {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function StripeSettingsPage() {
  const { isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const [config,     setConfig]     = useState(null)
  const [dirty,      setDirty]      = useState({})   // fields the user typed into
  const [loading,    setLoading]    = useState(true)
  const [saving,     setSaving]     = useState(false)
  const [error,      setError]      = useState(null)
  const [message,    setMessage]    = useState(null)
  const [bootstrapping, setBootstrapping] = useState(false)

  const load = async () => {
    setLoading(true); setError(null)
    try {
      const res = await axios.get(`${API_URL}${API_BASE_PATH}/account/admin/stripe-config/`,
        { headers: authHeaders() })
      setConfig(res.data)
      setDirty({})
    } catch (e) {
      const d = e?.response?.data || {}
      setError(d.message || e?.message || 'Failed to load Stripe settings')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  const setField = (k, v) => setDirty((d) => ({ ...d, [k]: v }))

  const save = async () => {
    // Only send fields the user actually touched.
    const body = { ...dirty }
    // `undefined` values mean "user cancelled editing" — strip them.
    Object.keys(body).forEach((k) => body[k] === undefined && delete body[k])
    if (Object.keys(body).length === 0) {
      setMessage('Nothing to save.')
      setTimeout(() => setMessage(null), 2000)
      return
    }
    setSaving(true); setError(null); setMessage(null)
    try {
      const res = await axios.patch(`${API_URL}${API_BASE_PATH}/account/admin/stripe-config/`,
        body, { headers: authHeaders() })
      setConfig(res.data); setDirty({})
      setMessage('Saved.')
      setTimeout(() => setMessage(null), 2500)
    } catch (e) {
      const d = e?.response?.data || {}
      setError(d.message || e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const runBootstrap = async () => {
    if (!window.confirm('Run bootstrap? This creates (or reuses) Stripe Products + Prices for every AccountType tier using the currently saved Secret Key. Safe to run multiple times.')) return
    setBootstrapping(true); setError(null); setMessage(null)
    try {
      const res = await axios.post(`${API_URL}${API_BASE_PATH}/account/admin/stripe-config/bootstrap/`,
        {}, { headers: authHeaders() })
      setConfig(res.data.config)
      setDirty({})
      const created = Object.entries(res.data.created_or_reused || {})
        .map(([k, v]) => `${k}: ${v}`).join(', ')
      setMessage(`Bootstrap complete. ${created}`)
      setTimeout(() => setMessage(null), 6000)
    } catch (e) {
      const d = e?.response?.data || {}
      setError(d.message || e?.message || 'Bootstrap failed')
    } finally {
      setBootstrapping(false)
    }
  }

  return (
    <>
      <Navigation />
      {isAuthenticated && <AppHeader />}
      <div className="min-h-screen bg-gray-100">
        <div className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                <h1 className="text-lg font-bold text-gray-800">Stripe Settings</h1>
                <button type="button" onClick={() => navigate('/account')}
                        className="text-sm text-[#358ECD] hover:underline">
                  ← Back to Account
                </button>
              </div>

              {error && (
                <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
                </div>
              )}
              {message && (
                <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded text-sm text-green-800 flex items-center gap-2">
                  <Check className="w-4 h-4" /> {message}
                </div>
              )}

              {loading ? (
                <div className="py-10 text-center text-sm text-gray-500">
                  <Loader className="inline w-4 h-4 animate-spin mr-1" /> Loading…
                </div>
              ) : !config ? (
                <p className="text-sm text-gray-500 italic">No config row yet.</p>
              ) : (
                <div className="space-y-5">
                  {/* Mode toggle */}
                  <div>
                    <div className="text-xs font-semibold text-gray-700 mb-1">Mode</div>
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={dirty.is_test_mode ?? config.is_test_mode}
                        onChange={(e) => setField('is_test_mode', e.target.checked)}
                      />
                      Test mode (use <code className="px-1 bg-gray-100 rounded text-xs">sk_test_...</code>/<code className="px-1 bg-gray-100 rounded text-xs">pk_test_...</code> keys)
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Turn this off for production. Test-mode and live-mode Products/Prices are separate on Stripe; re-run Bootstrap after switching.
                    </p>
                  </div>

                  {/* Keys */}
                  <div className="space-y-4 pt-1 border-t border-gray-100">
                    <SecretField
                      label="Secret Key"
                      meta={config.secret_key}
                      value={dirty.secret_key}
                      onChange={(v) => setField('secret_key', v)}
                      placeholder="sk_test_... or sk_live_..."
                      helpText="Reveals server-side operations. Store the plaintext only in the Stripe Dashboard and your password vault; never in git."
                    />

                    <TextField
                      label="Publishable Key"
                      value={dirty.publishable_key ?? config.publishable_key}
                      onChange={(v) => setField('publishable_key', v)}
                      placeholder="pk_test_... or pk_live_..."
                      helpText="Safe to expose to the browser. Currently unused by our flow (Checkout is fully hosted), but store it here in case we adopt Elements later."
                    />

                    <SecretField
                      label="Webhook Signing Secret"
                      meta={config.webhook_secret}
                      value={dirty.webhook_secret}
                      onChange={(v) => setField('webhook_secret', v)}
                      placeholder="whsec_..."
                      helpText="Copy from the Stripe Dashboard when you create the webhook endpoint. Required — the webhook returns 503 until this is set."
                    />
                  </div>

                  {/* Price IDs — filled in by Bootstrap, but editable */}
                  <div className="space-y-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-gray-700">Price IDs</div>
                        <p className="text-xs text-gray-500">One per tier. Run Bootstrap below to populate automatically.</p>
                      </div>
                      <button type="button" onClick={runBootstrap} disabled={bootstrapping}
                              className="px-3 py-1.5 bg-[#358ECD] hover:bg-[#2A78B0] text-white text-xs font-semibold rounded inline-flex items-center gap-1 disabled:opacity-50">
                        {bootstrapping ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
                        Bootstrap
                      </button>
                    </div>
                    <TextField label="Basic"        value={dirty.price_basic        ?? config.price_basic}        onChange={(v) => setField('price_basic', v)}        placeholder="price_..." />
                    <TextField label="Advanced"     value={dirty.price_advanced     ?? config.price_advanced}     onChange={(v) => setField('price_advanced', v)}     placeholder="price_..." />
                    <TextField label="Professional" value={dirty.price_professional ?? config.price_professional} onChange={(v) => setField('price_professional', v)} placeholder="price_..." />
                    <TextField label="Enterprise"   value={dirty.price_enterprise   ?? config.price_enterprise}   onChange={(v) => setField('price_enterprise', v)}   placeholder="price_..." />
                  </div>

                  {/* Redirect URLs */}
                  <div className="space-y-3 pt-3 border-t border-gray-100">
                    <TextField
                      label="Checkout Success URL"
                      value={dirty.success_url ?? config.success_url}
                      onChange={(v) => setField('success_url', v)}
                      placeholder="https://stage.wqdatalive.com/plans?checkout=success&session_id={CHECKOUT_SESSION_ID}"
                      helpText="Where Stripe returns users after a successful checkout. Keep the {CHECKOUT_SESSION_ID} placeholder verbatim."
                    />
                    <TextField
                      label="Checkout Cancel URL"
                      value={dirty.cancel_url ?? config.cancel_url}
                      onChange={(v) => setField('cancel_url', v)}
                      placeholder="https://stage.wqdatalive.com/plans?checkout=cancelled"
                      helpText="Where Stripe returns users if they cancel checkout."
                    />
                  </div>

                  <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      {config.updated_at ? <>Last updated {config.updated_at}{config.updated_by ? ` by ${config.updated_by}` : ''}</> : ''}
                    </div>
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => { load() }}
                              className="text-sm text-gray-700 hover:underline">
                        Reload
                      </button>
                      <button type="button" onClick={save} disabled={saving || Object.keys(dirty).length === 0}
                              className="px-5 py-1.5 bg-[#358ECD] hover:bg-[#2A78B0] text-white text-sm font-semibold rounded disabled:opacity-50 inline-flex items-center gap-1">
                        {saving && <Loader className="w-3.5 h-3.5 animate-spin" />}
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
